import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { updateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { getTacticsToAdd, getTacticsToRemove, shouldIntegrationCrosspost } from '../utils'
import { PROVIDER_TO_CONTENTS_FIELD, getContentsStructureForProvider } from 'screens/BrandPlanSchedule/utils'
import { BlogPlatformIntegration, EmailPlatformIntegration } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

type Action = 'add_tactics' | 'add_contents' | 'remove'

/**
 * Hook that provides a function to update the brand plan when adding or removing an integration.
 * Handles tactics, content counts, and platform selections.
 */
export const useIntegrationChangeUpdateBrandPlan = () => {
  const { data: brandPlan } = useCurrentBrandPlan()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  const updateBrandPlanForIntegration = useCallback(
    async (provider: ExternalIntegrationEnum, count: number) => {
      if (!brandPlan?.id || !workspaceId) {
        return
      }

      const contentsFieldName = PROVIDER_TO_CONTENTS_FIELD[provider]
      const contents = getContentsStructureForProvider(provider, count)

      let action: Action
      if (count > 0) {
        if (shouldIntegrationCrosspost(brandPlan, provider)) {
          action = 'add_tactics'
        } else {
          action = 'add_contents'
        }
      } else {
        action = 'remove'
      }

      let updatedTactics: typeof brandPlan.tactics | undefined = undefined
      let blogPlatform: BlogPlatformIntegration | undefined
      let emailPlatform: EmailPlatformIntegration | undefined
      let clearBlogPlatform = false
      let clearEmailPlatform = false

      if (action === 'add_tactics') {
        const { tactics: tacticsToAdd } = getTacticsToAdd(provider)
        updatedTactics = [...new Set([...brandPlan.tactics, ...tacticsToAdd])]
      } else if (action === 'add_contents') {
        const { setBlogPlatform, setEmailPlatform } = getTacticsToAdd(provider)
        blogPlatform = setBlogPlatform as BlogPlatformIntegration | undefined
        emailPlatform = setEmailPlatform as EmailPlatformIntegration | undefined
      } else if (action === 'remove') {
        // Remove tactics from the brand plan
        const {
          tactics: tacticsToRemove,
          clearBlogPlatform: shouldClearBlog,
          clearEmailPlatform: shouldClearEmail,
        } = getTacticsToRemove(provider)
        updatedTactics = brandPlan.tactics.filter((tactic) => !tacticsToRemove.includes(tactic))
        clearBlogPlatform = shouldClearBlog ?? false
        clearEmailPlatform = shouldClearEmail ?? false
      }

      const updatePayload: Parameters<typeof updateBrandPlan>[0] = {
        id: brandPlan.id,
        workspaceId,
        also_update_schedules: true,
      }

      // Only include tactics if we're updating them (not when adding contents only)
      if (action !== 'add_contents' && updatedTactics !== undefined) {
        updatePayload.tactics = updatedTactics
      }

      // only update contents if not adding tactics since crossposting should already have contents set
      if (contentsFieldName && contents && action !== 'add_tactics') {
        updatePayload[contentsFieldName] = contents
      }

      if (action === 'add_contents') {
        if (blogPlatform) {
          updatePayload.selected_blog_platform = blogPlatform
        }
        if (emailPlatform) {
          updatePayload.selected_email_platform = emailPlatform
        }
      } else {
        if (clearBlogPlatform) {
          updatePayload.selected_blog_platform = 'generic'
        }
        if (clearEmailPlatform) {
          updatePayload.selected_email_platform = 'generic'
        }
      }

      await updateBrandPlan(updatePayload)

      // Invalidate the brand plan query to refresh the data
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandPlans', 'current'])
    },
    [brandPlan, workspaceId, queryClient],
  )

  return updateBrandPlanForIntegration
}
